import { useMutation, useQueryClient } from '@tanstack/react-query';

import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { patchVotable } from '@/lib/vote-cache';
import { type VoteDirection } from '@/lib/vote-math';

// One hook for both vote targets -- the target type is the only
// difference between the endpoint pairs (issue #241).
export function useVote(targetType: 'thread' | 'post') {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, direction }: { id: string; direction?: VoteDirection }) => {
      if (direction === undefined) {
        return targetType === 'thread' ? api.removeThreadVote(id) : api.removePostVote(id);
      }

      return targetType === 'thread' ? api.voteThread(id, direction) : api.votePost(id, direction);
    },
    onMutate: async ({ id, direction }) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.threads() });
      await queryClient.cancelQueries({ queryKey: queryKeys.thread() });

      // Snapshot every cache this patch touches, including all prefixed
      // keys (thread-detail caches the thread at ['thread', threadId]
      // and its replies at ['thread', threadId, 'posts']).
      const previousThreads = queryClient.getQueriesData({ queryKey: queryKeys.threads() });
      const previousThread = queryClient.getQueriesData({ queryKey: queryKeys.thread() });

      // Both families pass through the one pure shape-walker
      // (lib/vote-cache.ts, unit-tested): threads is an infinite-query
      // {pages} object, the thread a bare object, the replies a bare
      // array. One prefix pass per root covers all of them.
      queryClient.setQueriesData({ queryKey: queryKeys.threads() }, (data: unknown) =>
        patchVotable(data, id, direction),
      );
      queryClient.setQueriesData({ queryKey: queryKeys.thread() }, (data: unknown) =>
        patchVotable(data, id, direction),
      );

      return { previous: [...previousThreads, ...previousThread] };
    },
    onError: (_error, _variables, context) => {
      if (!context) return;

      // getQueriesData snapshots are [key, data] pairs -- restore each.
      context.previous.forEach(([key, data]) => {
        if (data !== undefined) {
          queryClient.setQueryData(key, data);
        }
      });
    },
    onSettled: () => {
      // Refetch after every settle so the server's authoritative score
      // (concurrent votes included) lands -- optimistic UI shows the
      // intent, the refetch shows the truth. The thread() prefix covers
      // the single response AND thread-detail's replies array.
      void queryClient.invalidateQueries({ queryKey: queryKeys.threads() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.thread() });
    },
  });
}
